/**
 * AIReasoningPanel - Shared component for displaying AI reasoning/thinking steps
 * Used across all AI chat interfaces for consistent UX
 */

import React, { useState, useEffect } from 'react';
import { Brain, CheckCircle, X, Loader2, ChevronDown } from 'lucide-react';
import PropTypes from 'prop-types';

/**
 * AIReasoningPanel Component
 * Displays AI reasoning steps with a consistent, beautiful UI
 *
 * @param {Object[]} steps - Array of reasoning steps
 * @param {string} steps[].stepId - Unique identifier for the step
 * @param {string} steps[].title - Main title/description of the step
 * @param {string} steps[].status - Status: 'in_progress', 'completed', or 'error'
 * @param {string[]} [steps[].items] - Optional sub-items/details for the step
 * @param {boolean} [isCollapsed=false] - Whether to show collapsed view initially
 * @param {string} [accentColor='sky'] - Accent color theme: 'sky', 'violet', 'fuchsia', 'emerald'
 */
const AIReasoningPanel = ({ steps, isCollapsed = false, accentColor = 'sky' }) => {
    const [expanded, setExpanded] = useState(!isCollapsed);

    // Auto-collapse when isCollapsed prop changes
    useEffect(() => {
        if (isCollapsed) setExpanded(false);
    }, [isCollapsed]);

    if (!steps || steps.length === 0) return null;

    const completedCount = steps.filter(s => s.status === 'completed').length;
    const totalCount = steps.length;
    const isAllComplete = completedCount === totalCount;

    // Color configurations for different themes
    const colorConfig = {
        sky: {
            iconBg: 'bg-sky-500/20',
            iconColor: 'text-sky-400',
            spinnerColor: 'text-sky-400',
            borderColor: 'border-sky-500/30',
            gradientFrom: 'from-sky-500/10',
            gradientTo: 'to-slate-800/80',
            headerBg: 'bg-gradient-to-r from-sky-900/50 to-slate-800/90',
        },
        violet: {
            iconBg: 'bg-violet-500/20',
            iconColor: 'text-violet-400',
            spinnerColor: 'text-violet-400',
            borderColor: 'border-violet-500/30',
            gradientFrom: 'from-violet-500/10',
            gradientTo: 'to-slate-800/80',
            headerBg: 'bg-gradient-to-r from-violet-900/50 to-slate-800/90',
        },
        fuchsia: {
            iconBg: 'bg-fuchsia-500/20',
            iconColor: 'text-fuchsia-400',
            spinnerColor: 'text-fuchsia-400',
            borderColor: 'border-fuchsia-500/30',
            gradientFrom: 'from-fuchsia-500/10',
            gradientTo: 'to-slate-800/80',
            headerBg: 'bg-gradient-to-r from-fuchsia-900/50 to-slate-800/90',
        },
        emerald: {
            iconBg: 'bg-emerald-500/20',
            iconColor: 'text-emerald-400',
            spinnerColor: 'text-emerald-400',
            borderColor: 'border-emerald-500/30',
            gradientFrom: 'from-emerald-500/10',
            gradientTo: 'to-slate-800/80',
            headerBg: 'bg-gradient-to-r from-emerald-900/50 to-slate-800/90',
        }
    };

    const colors = colorConfig[accentColor] || colorConfig.sky;

    // Collapsed summary button
    if (!expanded) {
        return (
            <button
                onClick={() => setExpanded(true)}
                className={`flex items-center gap-2 px-3 py-1.5 text-xs bg-slate-800/60 dark:bg-slate-800/60 bg-slate-100 rounded-full border ${colors.borderColor} dark:border-slate-700/50 border-slate-200 text-slate-400 hover:bg-slate-700/60 hover:dark:bg-slate-700/60 hover:bg-slate-200 transition-all backdrop-blur-sm`}
            >
                <div className={`p-0.5 rounded-full ${colors.iconBg}`}>
                    <Brain className={`w-3 h-3 ${colors.iconColor}`} />
                </div>
                <span>
                    {isAllComplete
                        ? `Analyzed in ${totalCount} steps`
                        : `${completedCount}/${totalCount} steps`
                    }
                </span>
                <ChevronDown className="w-3 h-3" />
            </button>
        );
    }

    return (
        <div className="mb-2">
            {/* Subtle inline header */}
            <div className="flex items-center gap-2 mb-2 px-1">
                <Brain className={`w-3.5 h-3.5 ${colors.iconColor}`} />
                <span className="text-[11px] font-medium text-slate-500 dark:text-slate-500 uppercase tracking-wider">
                    Thinking
                </span>
                <button
                    onClick={() => setExpanded(false)}
                    className="ml-auto p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700/50 text-slate-400 transition-colors"
                    aria-label="Hide"
                >
                    <X className="w-3 h-3" />
                </button>
            </div>

            {/* Steps List - subtle inline design */}
            <div className="space-y-1">
                {steps.map((step) => {
                    const isCompleted = step.status === 'completed';
                    const isError = step.status === 'error';

                    return (
                        <div
                            key={step.stepId}
                            className={`flex items-center gap-2 transition-opacity ${isCompleted ? 'opacity-60' : 'opacity-100'}`}
                        >
                            {/* Status Icon - smaller */}
                            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                                {isCompleted ? (
                                    <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                                ) : isError ? (
                                    <X className="w-3.5 h-3.5 text-red-400" />
                                ) : (
                                    <Loader2 className={`w-3.5 h-3.5 ${colors.spinnerColor} animate-spin`} />
                                )}
                            </div>

                            {/* Step Title */}
                            <span className={`text-xs ${
                                isCompleted
                                    ? 'text-slate-500 dark:text-slate-500'
                                    : isError
                                        ? 'text-red-400'
                                        : 'text-slate-600 dark:text-slate-400'
                            }`}>
                                {step.title}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

AIReasoningPanel.propTypes = {
    steps: PropTypes.arrayOf(PropTypes.shape({
        stepId: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        status: PropTypes.oneOf(['in_progress', 'completed', 'error']).isRequired,
        items: PropTypes.arrayOf(PropTypes.string)
    })),
    isCollapsed: PropTypes.bool,
    accentColor: PropTypes.oneOf(['sky', 'violet', 'fuchsia', 'emerald'])
};

export default AIReasoningPanel;
